/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Safe wrapper for Clerk's useUser hook that works in test mode
 *
 * This hook always calls Clerk's useUser unconditionally to comply with
 * React's rules of hooks, but returns mock values when test mode is active.
 */

import { DEFAULT_TEST_USERS } from "@/lib/testing/test-mode-simple";
import { useUser as useClerkUser } from "@clerk/nextjs";

// Test mode detection
// NOTE: We do NOT check NODE_ENV === "test" because that's true for both
// unit tests (Vitest) and E2E tests (Playwright). Unit tests should use
// mocked values, not test bypass. Only E2E tests should activate test mode.
const isTestModeActive = (): boolean => {
  const envTestMode =
    process.env["NEXT_PUBLIC_E2E_TEST_MODE"] === "true" ||
    process.env["E2E_TEST_MODE"] === "true";

  if (typeof window !== "undefined") {
    return envTestMode || Boolean(window.__TEST_BYPASS_ENABLED);
  }

  return envTestMode;
};

/**
 * Safe wrapper for useUser that works in test mode
 */
export function useUserSafe(): ReturnType<typeof useClerkUser> {
  // Always call hooks unconditionally to comply with rules of hooks
  const clerkUser = useClerkUser();
  const testModeActive = isTestModeActive();

  if (testModeActive) {
    // Return mock user values for test mode
    const testUser = DEFAULT_TEST_USERS.owner;

    // Type assertion is safe here - we're providing a test implementation that satisfies the interface
    return ({
      isLoaded: true,
      isSignedIn: true,
      user: {
        id: testUser.id,
        firstName: testUser.firstName,
        lastName: testUser.lastName,
        emailAddresses: [{ emailAddress: testUser.email }],
        publicMetadata: { role: testUser.role },
        organizationMemberships: [
          {
            organization: {
              id: testUser.organizationId,
              name: "Test Organization",
            },
          },
        ],
      },
    } as unknown as ReturnType<typeof useClerkUser>);
  }

  // In normal mode, return Clerk's useUser result
  return clerkUser;
}
